import { useClearableReducer } from "@client-shared/hooks/use-clearable-reducer";
import { ORDER_STATUS_COUNTS_QUERY_KEY } from "@bcc/hooks/use-order-status-counts";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { PURCHASE_ORDERS_QUERY_KEY } from "@client-shared/providers/purchase-orders";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { LabelPrintBatchStatus } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { createInitialBatchesState, reducer } from "./batches-context.actions";
import type { BatchesContextState, BatchesInitializeOptions, BatchesUtils } from "./batches-context.types";
import {
  BCC_BATCH_DEFAULT_LIMIT,
  BCC_BATCH_POLLING_INTERVAL_MS,
  createBatchHandler,
  fetchBatchHandler,
  fetchBatchesHandler,
  getLabelPrintBatchShippingDocumentHandler,
  getLabelPrintBatchShippingDocumentUrlHandler,
  isLabelPrintBatchProcessing,
} from "./batches-context.utils";

const initialState = createInitialBatchesState();

const BCC_BATCH_STATUS_VALUES = ["Processing", "Failed-for-Retry", "Completed", "Failed", "PartiallyFailed"] as const;

const resolveSummaryParams = (vendorKey: string, params?: BatchesInitializeOptions["params"]) => ({
  ...params,
  vendorKey,
  limit: params?.limit ?? BCC_BATCH_DEFAULT_LIMIT,
});

const areSummaryParamsEqual = (left?: BatchesInitializeOptions["params"], right?: BatchesInitializeOptions["params"]): boolean => {
  return JSON.stringify(left) === JSON.stringify(right);
};

const getBatchStatusParam = (value?: string | null): LabelPrintBatchStatus | undefined => {
  if (value && BCC_BATCH_STATUS_VALUES.includes(value as (typeof BCC_BATCH_STATUS_VALUES)[number])) {
    return value as LabelPrintBatchStatus;
  }

  return undefined;
};

export const BatchesContext = createContext<BatchesContextState | undefined>(undefined);

interface BatchesProviderProps {
  readonly children: ReactNode;
}

export function BatchesProvider({ children }: BatchesProviderProps) {
  const [vendorState] = useVendorContext();
  const vendorKey = vendorState.currentVendor?.vendorKey;
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [state, dispatch, resetState] = useClearableReducer(reducer, initialState);
  const queryClient = useQueryClient();
  const wasProcessingBatchRef = useRef(false);
  const labelCreatedCountRef = useRef<number | undefined>(undefined);

  const activeTab = searchParams.get("tab");
  const batchIdFromUrl = searchParams.get("batchId") ?? undefined;
  const batchStatusFromUrl = getBatchStatusParam(searchParams.get("batchStatus"));
  const isBatchesRoute = location.pathname === ROUTE_PATHS.BATCHES;
  const isVendorReady = Boolean(vendorState.initialized && vendorKey);
  const shouldLoadBatches = Boolean(isVendorReady && (activeTab === "batches" || batchIdFromUrl || isBatchesRoute));
  const isProcessingBatch = isLabelPrintBatchProcessing(state.batch);

  useEffect(() => {
    dispatch({ type: "SET_INITIALIZED", payload: isVendorReady });
  }, [isVendorReady, dispatch]);

  useEffect(() => {
    if (state.batchId === batchIdFromUrl) {
      return;
    }
    if (!batchIdFromUrl && isProcessingBatch) {
      return;
    }
    dispatch({ type: "SET_BATCH_ID", payload: batchIdFromUrl });
  }, [batchIdFromUrl, isProcessingBatch, state.batchId, dispatch]);

  useEffect(() => {
    if (batchIdFromUrl || isProcessingBatch) {
      return;
    }
    dispatch({ type: "SET_BATCH", payload: undefined });
    dispatch({ type: "SET_PROCESSING", payload: false });
  }, [batchIdFromUrl, isProcessingBatch, dispatch]);

  useEffect(() => {
    if (!vendorKey) {
      return;
    }

    const resolvedParams = resolveSummaryParams(vendorKey, {
      batchStatus: batchStatusFromUrl,
      limit: state.params?.limit,
      max: state.params?.max,
    });

    if (!areSummaryParamsEqual(state.params, resolvedParams)) {
      dispatch({ type: "SET_PARAMS", payload: resolvedParams });
    }
  }, [batchStatusFromUrl, dispatch, state.params, vendorKey]);

  const loadBatches = useCallback(
    async (params?: BatchesInitializeOptions["params"]) => {
      if (!vendorKey) {
        return [];
      }

      const resolvedParams = resolveSummaryParams(vendorKey, params);
      return fetchBatchesHandler({
        dispatch,
        params: resolvedParams,
        vendorKey,
      });
    },
    [dispatch, vendorKey]
  );

  const fetchBatch = useCallback(
    async (batchId: string) => {
      return fetchBatchHandler({
        batchId,
        dispatch,
      });
    },
    [dispatch]
  );

  const fetchBatches = useCallback<BatchesUtils["fetchBatches"]>(
    (params) => {
      if (!vendorKey) {
        return Promise.resolve([]);
      }

      const resolvedParams = resolveSummaryParams(vendorKey, params);

      if (!areSummaryParamsEqual(state.params, resolvedParams)) {
        dispatch({ type: "SET_PARAMS", payload: resolvedParams });
      }

      return loadBatches(resolvedParams);
    },
    [dispatch, loadBatches, state.params, vendorKey]
  );

  const createBatch = useCallback<BatchesUtils["createBatch"]>(
    async (batch) => {
      if (!vendorKey) {
        return undefined;
      }

      const createdBatch = await createBatchHandler({
        batch,
        dispatch,
      });

      await loadBatches(state.params);

      return createdBatch;
    },
    [dispatch, loadBatches, state.params, vendorKey]
  );

  const clearBatch = useCallback(() => {
    dispatch({ type: "SET_BATCH", payload: undefined });
    dispatch({ type: "SET_BATCH_ID", payload: undefined });
    dispatch({ type: "SET_PROCESSING", payload: false });
  }, [dispatch]);

  const clearCache = useCallback<BatchesUtils["clearCache"]>(async () => {
    if (!vendorKey) {
      return;
    }

    await loadBatches(state.params);

    if (state.batchId) {
      await fetchBatch(state.batchId);
    }
  }, [fetchBatch, loadBatches, state.batchId, state.params, vendorKey]);

  const initialize = useCallback<BatchesUtils["initialize"]>(
    async (options) => {
      if (!vendorKey) {
        return;
      }

      const resolvedParams = resolveSummaryParams(vendorKey, options.params ?? state.params);

      if (!areSummaryParamsEqual(state.params, resolvedParams)) {
        dispatch({ type: "SET_PARAMS", payload: resolvedParams });
      }

      if (options.batchId !== undefined && options.batchId !== state.batchId) {
        dispatch({ type: "SET_BATCH_ID", payload: options.batchId });
      }

      if (shouldLoadBatches) {
        await loadBatches(resolvedParams);
      }

      if (options.batchId) {
        await fetchBatch(options.batchId);
      }
    },
    [dispatch, fetchBatch, loadBatches, shouldLoadBatches, state.batchId, state.params, vendorKey]
  );

  const getLabelPrintBatchShippingDocument = useCallback<BatchesUtils["getLabelPrintBatchShippingDocument"]>(
    async (batch, fileType) => {
      return getLabelPrintBatchShippingDocumentHandler({
        batch,
        dispatch,
        fileType,
      });
    },
    [dispatch]
  );

  const getLabelPrintBatchShippingDocumentUrl = useCallback<BatchesUtils["getLabelPrintBatchShippingDocumentUrl"]>(
    async (batch, fileType) => {
      return getLabelPrintBatchShippingDocumentUrlHandler({
        batch,
        dispatch,
        fileType,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!shouldLoadBatches || !state.params) {
      return;
    }

    void loadBatches(state.params);
  }, [loadBatches, shouldLoadBatches, state.params]);

  useEffect(() => {
    if (!isVendorReady || !batchIdFromUrl || state.batch?.batchId === batchIdFromUrl) {
      return;
    }

    void fetchBatch(batchIdFromUrl);
  }, [batchIdFromUrl, fetchBatch, isVendorReady, state.batch?.batchId]);

  useEffect(() => {
    if (!state.batchId || !isProcessingBatch) {
      return;
    }

    const intervalId = globalThis.setInterval(() => {
      void fetchBatch(state.batchId as string);
    }, BCC_BATCH_POLLING_INTERVAL_MS);

    return () => {
      globalThis.clearInterval(intervalId);
    };
  }, [fetchBatch, isProcessingBatch, state.batchId]);

  useEffect(() => {
    if (wasProcessingBatchRef.current && !isProcessingBatch) {
      void queryClient.invalidateQueries({
        queryKey: [PURCHASE_ORDERS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ORDER_STATUS_COUNTS_QUERY_KEY],
      });
    }
    wasProcessingBatchRef.current = isProcessingBatch;
  }, [isProcessingBatch, queryClient]);

  const clearState = useCallback(() => {
    wasProcessingBatchRef.current = false;
    resetState();
  }, [resetState]);

  // the orders that got their label while the batch is retried show it right away, not only when the batch has finished
  const labelCreatedCount = state.batch?.labelCreatedOrderCount ?? state.batch?.labelCreatedOrders;
  useEffect(() => {
    const previousCount = labelCreatedCountRef.current;
    labelCreatedCountRef.current = labelCreatedCount;
    if (isProcessingBatch && previousCount !== undefined && labelCreatedCount !== previousCount) {
      void queryClient.invalidateQueries({ queryKey: [PURCHASE_ORDERS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ORDER_STATUS_COUNTS_QUERY_KEY] });
    }
  }, [isProcessingBatch, labelCreatedCount, queryClient]);

  const utils = useMemo<BatchesUtils>(
    () => ({
      clearState,
      initialize,
      fetchBatches,
      fetchBatch,
      createBatch,
      clearBatch,
      clearCache,
      getLabelPrintBatchShippingDocument,
      getLabelPrintBatchShippingDocumentUrl,
    }),
    [
      clearState,
      clearBatch,
      clearCache,
      createBatch,
      fetchBatch,
      fetchBatches,
      getLabelPrintBatchShippingDocument,
      getLabelPrintBatchShippingDocumentUrl,
      initialize,
    ]
  );

  const value = useMemo<BatchesContextState>(() => [state, dispatch, utils], [state, utils, dispatch]);

  return <BatchesContext.Provider value={value}>{children}</BatchesContext.Provider>;
}

export function useBatchesContext(): BatchesContextState {
  const context = useContext(BatchesContext);

  if (!context) {
    throw new Error("useBatchesContext must be used within a BatchesProvider");
  }

  return context;
}
